import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import '../../App.css';

function RegistrationCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAll, setShowAll] = useState(true);
  const [newCode, setNewCode] = useState({
    maxUses: '',
    expiryDays: ''
  });
  const navigate = useNavigate();

  const fetchCodes = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/management/registration-codes${showAll ? '?showAll=true' : ''}`);
      setCodes(response.data.data.codes || []);
    } catch (err) {
      console.error('Error fetching registration codes:', err);
      setError(err.response?.data?.error || 'Failed to load registration codes');
      if (err.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
      }
    } finally {
      setLoading(false);
    }
  }, [showAll]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleCreateCode = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      const payload = {};
      
      if (newCode.maxUses && parseInt(newCode.maxUses) > 0) {
        payload.maxUses = parseInt(newCode.maxUses);
      }
      
      if (newCode.expiryDays && parseInt(newCode.expiryDays) > 0) {
        payload.expiresInDays = parseInt(newCode.expiryDays);
      }

      const response = await api.post('/management/registration-codes', payload);
      console.log('Registration code response:', response.data);
      
      const code = response.data?.data?.code;
      if (!code) {
        throw new Error('No code returned from server');
      }
      
      alert(`Registration code created!\n\nCode: ${code}\n\nPlease copy this code now. It will be shown in the list below.`);
      
      setNewCode({ maxUses: '', expiryDays: '' });
      setShowCreateForm(false);
      await fetchCodes();
    } catch (err) {
      console.error('Error creating registration code:', err);
      setError(err.response?.data?.error || 'Failed to create registration code');
    }
  };

  const handleDeleteCode = async (codeId, code) => {
    if (!window.confirm(`Are you sure you want to delete the code "${code}"?\n\nThis will prevent new users from using this code for registration.`)) {
      return;
    }

    try {
      setError('');
      await api.delete(`/management/delete-code/${codeId}`);
      await fetchCodes();
      alert('Registration code deleted successfully.');
    } catch (err) {
      console.error('Error deleting registration code:', err);
      setError(err.response?.data?.error || 'Failed to delete registration code');
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code)
      .then(() => {
        alert(`Code "${code}" copied to clipboard!`);
      })
      .catch(() => {
        alert(`Code: ${code}\n\nPlease copy manually.`);
      });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const getStatusBadge = (code) => {
    if (!code.isActive) {
      return <span className="status-badge status-inactive">Inactive</span>;
    }
    if (isExpired(code.expiresAt)) {
      return <span className="status-badge status-expired">Expired</span>;
    }
    if (code.maxUses && code.usedCount >= code.maxUses) {
      return <span className="status-badge status-exhausted">Exhausted</span>;
    }
    return <span className="status-badge status-active">Active</span>;
  };

  return (
    <div className="container">
      <div className="page-header">
        <h1>Registration Codes</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/admin')}>
          Back to Dashboard
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="actions-section">
        <button 
          className="btn btn-primary"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : 'Create New Code'}
        </button>
        <button
          className={`btn ${showAll ? 'btn-secondary' : 'btn-outline'}`}
          onClick={() => setShowAll(!showAll)}
          style={{ marginLeft: '1rem' }}
        >
          {showAll ? 'Active Only' : 'Show All'}
        </button>
      </div>

      {showCreateForm && (
        <div className="create-form-container">
          <h2>Create Registration Code</h2>
          <form onSubmit={handleCreateCode} className="create-form">
            <div className="form-group">
              <label htmlFor="maxUses">
                Max Uses (optional):
                <span className="help-text">Leave empty for unlimited uses</span>
              </label>
              <input
                type="number"
                id="maxUses"
                min="1"
                value={newCode.maxUses}
                onChange={(e) => setNewCode({...newCode, maxUses: e.target.value})}
                placeholder="e.g., 10"
              />
            </div>

            <div className="form-group">
              <label htmlFor="expiryDays">
                Expiry (days):
                <span className="help-text">Leave empty for no expiration</span>
              </label>
              <input
                type="number"
                id="expiryDays"
                min="1"
                value={newCode.expiryDays}
                onChange={(e) => setNewCode({...newCode, expiryDays: e.target.value})}
                placeholder="e.g., 30"
              />
            </div>

            <button type="submit" className="btn btn-success">Generate Code</button>
          </form>
        </div>
      )}

      {loading ? (
        <p>Loading registration codes...</p>
      ) : (
        <>
          <p className="code-count">Total: {codes.length} code{codes.length !== 1 ? 's' : ''}</p>
          
          <div className="table-container">
            <table className="codes-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Status</th>
                  <th>Used / Max</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{textAlign: 'center'}}>No registration codes found</td>
                  </tr>
                ) : (
                  codes.map(code => (
                    <tr key={code.id} className={!code.isActive || isExpired(code.expiresAt) ? 'inactive-row' : ''}>
                      <td>
                        <code className="code-display">{code.code}</code>
                        <button
                          className="btn-icon"
                          onClick={() => copyToClipboard(code.code)}
                          title="Copy to clipboard"
                        >
                          📋
                        </button>
                      </td>
                      <td>{getStatusBadge(code)}</td>
                      <td>
                        {code.usedCount} / {code.maxUses || '∞'}
                      </td>
                      <td>{formatDate(code.createdAt)}</td>
                      <td>{formatDate(code.expiresAt)}</td>
                      <td>
                        {code.isActive && (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteCode(code.id, code.code)}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <style jsx="true">{`
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
        }

        .actions-section {
          margin-bottom: 2rem;
        }

        .create-form-container {
          background: white;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          padding: 2rem;
          margin-bottom: 2rem;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .create-form-container h2 {
          margin-top: 0;
        }

        .create-form {
          max-width: 500px;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
        }

        .help-text {
          display: block;
          font-size: 0.875rem;
          color: #666;
          font-weight: normal;
          margin-top: 0.25rem;
        }

        .form-group input {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 1rem;
        }

        .code-count {
          margin-bottom: 1rem;
          color: #666;
        }

        .table-container {
          overflow-x: auto;
        }

        .codes-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .codes-table th,
        .codes-table td {
          padding: 0.75rem;
          text-align: left;
          border-bottom: 1px solid #e0e0e0;
        }

        .codes-table th {
          background-color: #f5f5f5;
          font-weight: 600;
          color: #333;
        }

        .codes-table tbody tr:hover {
          background-color: #f9f9f9;
        }

        .inactive-row {
          opacity: 0.6;
        }

        .code-display {
          font-family: 'Courier New', monospace;
          background-color: #f5f5f5;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.9rem;
          margin-right: 0.5rem;
        }

        .btn-icon {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0.25rem;
          transition: transform 0.2s;
        }

        .btn-icon:hover {
          transform: scale(1.2);
        }

        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .status-active {
          background-color: #d4edda;
          color: #155724;
        }

        .status-inactive {
          background-color: #e2e3e5;
          color: #383d41;
        }

        .status-expired {
          background-color: #f8d7da;
          color: #721c24;
        }

        .status-exhausted {
          background-color: #fff3cd;
          color: #856404;
        }

        .btn {
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.3s ease;
        }

        .btn-sm {
          padding: 0.375rem 0.75rem;
          font-size: 0.8125rem;
        }

        .btn-primary {
          background-color: #007bff;
          color: white;
        }

        .btn-primary:hover {
          background-color: #0056b3;
        }

        .btn-secondary {
          background-color: #6c757d;
          color: white;
        }

        .btn-secondary:hover {
          background-color: #545b62;
        }

        .btn-success {
          background-color: #28a745;
          color: white;
        }

        .btn-success:hover {
          background-color: #218838;
        }

        .btn-danger {
          background-color: #dc3545;
          color: white;
        }

        .btn-danger:hover {
          background-color: #c82333;
        }
      `}</style>
    </div>
  );
}

export default RegistrationCodes;
